import React, { useState, useEffect } from 'react';
import { 
  Users, 
  ChevronDown, 
  LogOut, 
  Lock, 
  Eye, 
  EyeOff,
  UserCheck,
  X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import apiClient from '../../api/config';
import toast from 'react-hot-toast';
import { handleApiError } from '../../utils/errorHandler';

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  name?: string; // For backward compatibility
  email?: string;
  phone_number: string;
  role: string;
  is_active: boolean;
  last_login?: string;
}

interface EmployeeSwitcherProps {
  className?: string;
}

const EmployeeSwitcher: React.FC<EmployeeSwitcherProps> = ({ className = '' }) => {
  const { user, logout } = useAuth();
  const { t, language } = useLanguage();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  useEffect(() => {
    if (showDropdown && employees.length === 0) {
      fetchEmployees();
    }
  }, [showDropdown]);

  const fetchEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const response = await apiClient.get('/employees/switch-list');
      setEmployees(response.data.employees || []);
    } catch (error) {
      const processedError = handleApiError(error);
      toast.error(processedError.message);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handleEmployeeSelect = (employee: Employee) => {
    if (employee.id === user?.id) {
      setShowDropdown(false);
      return;
    }
    setSelectedEmployee(employee);
    setShowDropdown(false);
    setShowPasswordModal(true);
    setPassword('');
  };

  const handleSwitchEmployee = async () => {
    if (!selectedEmployee || !password.trim()) {
      toast.error(t('pleaseEnterPassword'));
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post('/auth/switch-employee', {
        employee_id: selectedEmployee.id,
        password: password
      });

      // Update auth context with new user data and tokens
      const { user: newUser, access_token, refresh_token } = response.data;
      
      // Store new tokens
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('user', JSON.stringify(newUser));

      toast.success(`Switched to ${selectedEmployee.name}`);
      
      // Close modal and refresh page to update context
      setShowPasswordModal(false);
      setSelectedEmployee(null);
      setPassword('');
      
      // Refresh the page to update the auth context
      window.location.reload();
    } catch (error) {
      const processedError = handleApiError(error);
      toast.error(processedError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setShowDropdown(false);
    logout();
  };

  const closeModal = () => {
    setShowPasswordModal(false);
    setSelectedEmployee(null);
    setPassword('');
    setShowPassword(false);
  };

  if (!user || user.role === 'customer') {
    return null;
  }

  return (
    <>
      <div className={`relative ${className}`}>
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="flex items-center px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <div className="flex items-center space-x-1 sm:space-x-2">
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs sm:text-sm font-medium">
                {user.first_name?.charAt(0).toUpperCase() || user.last_name?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div className={`${language === 'ar' ? 'text-right' : 'text-left'} min-w-0`}>
              <div className="font-medium truncate max-w-[100px] sm:max-w-[150px] lg:max-w-none">{user.first_name} {user.last_name}</div>
              <div className="text-xs text-gray-500 capitalize hidden sm:block">{user.role.replace('_', ' ')}</div>
            </div>
            <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
          </div>
        </button>

        {showDropdown && (
          <div className="absolute right-0 top-full mt-2 w-72 sm:w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
            <div className="p-3 border-b border-gray-100">
              <div className="flex items-center text-sm font-medium text-gray-700">
                <Users className="h-4 w-4 mr-2" />
                {t('switchEmployee')}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {t('selectEmployeeToSwitchTo')}
              </div>
            </div>

            <div className="max-h-64 overflow-y-auto">
              {loadingEmployees ? (
                <div className="p-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600 mx-auto mb-2"></div>
                  <span className="text-sm text-gray-500">{t('loadingEmployees')}</span>
                </div>
              ) : employees.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  {t('noOtherEmployeesFound')}
                </div>
              ) : (
                <div className="py-1">
                  {employees.map((employee) => (
                    <button
                      key={employee.id}
                      onClick={() => handleEmployeeSelect(employee)}
                      disabled={employee.id === user.id}
                      className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-left hover:bg-gray-50 flex items-center ${
                        employee.id === user.id 
                          ? 'bg-teal-50 cursor-default' 
                          : 'cursor-pointer'
                      } ${!employee.is_active ? 'opacity-50' : ''}`}
                    >
                      <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-300 rounded-full flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                        <span className="text-gray-600 text-xs sm:text-sm font-medium">
                          {employee.first_name?.charAt(0).toUpperCase() || employee.last_name?.charAt(0).toUpperCase() || 'E'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center">
                          <span className="font-medium text-sm sm:text-base text-gray-900 truncate">
                            {employee.first_name} {employee.last_name}
                          </span>
                          {employee.id === user.id && (
                            <UserCheck className="h-3 w-3 sm:h-4 sm:w-4 ml-2 text-teal-600 flex-shrink-0" />
                          )}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-500 capitalize">
                          {employee.role.replace('_', ' ')}
                        </div>
                        {employee.last_login && (
                          <div className="text-xs text-gray-400 hidden sm:block">
                            {t('lastLogin')}: {new Date(employee.last_login).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      {!employee.is_active && (
                        <div className="text-xs text-red-500 font-medium">
                          {t('inactive')}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 p-1.5 sm:p-2">
              <button
                onClick={handleLogout}
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-left text-xs sm:text-sm text-red-600 hover:bg-red-50 rounded-md flex items-center"
              >
                <LogOut className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                {t('logout')}
              </button>
            </div>
          </div>
        )}

        {/* Click outside to close */}
        {showDropdown && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowDropdown(false)}
          />
        )}
      </div>

      {/* Password Verification Modal */}
      {showPasswordModal && selectedEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <Lock className="h-5 w-5 mr-2 text-teal-600" />
                {t('verifyPassword')}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4">
              <div className="bg-gray-50 p-2.5 sm:p-3 rounded-lg mb-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-300 rounded-full flex items-center justify-center mr-2.5 sm:mr-3 flex-shrink-0">
                    <span className="text-gray-600 text-sm sm:text-base font-medium">
                      {selectedEmployee.first_name?.charAt(0).toUpperCase() || selectedEmployee.last_name?.charAt(0).toUpperCase() || 'E'}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-sm sm:text-base text-gray-800 truncate">
                      {selectedEmployee.first_name} {selectedEmployee.last_name}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-500 capitalize">
                      {selectedEmployee.role.replace('_', ' ')}
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Enter password to switch to {selectedEmployee.first_name} {selectedEmployee.last_name}
              </p>

              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSwitchEmployee()}
                  placeholder={t('password')}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end space-x-2 sm:space-x-3">
              <button
                onClick={closeModal}
                className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleSwitchEmployee}
                disabled={loading || !password.trim()}
                className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white mr-1.5 sm:mr-2"></div>
                ) : (
                  <UserCheck className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                )}
                {loading ? t('switching') : t('switchEmployee')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmployeeSwitcher;